import { onBeforeUnmount, type Ref, ref } from 'vue'

export interface UseFetchItems<T, A extends any[]> {
  data: Ref<T | null>
  error: Ref<string | null>
  isLoading: Ref<boolean>
  execute: (...args: A) => Promise<void>
  clear: () => void
}

function useFetch<T, A extends any[] = []>(
  callbackFn: (signal: AbortSignal, ...args: A) => Promise<T>
): UseFetchItems<T, A> {
  const data = ref<T | null>(null) as Ref<T | null>
  const error = ref<string | null>(null)
  const isLoading = ref(false)
  let controller: AbortController | null = null

  async function execute(...args: A): Promise<void> {
    isLoading.value = true
    error.value = null
    controller?.abort()

    controller = new AbortController()
    const signal = controller.signal

    try {
      data.value = await callbackFn(signal, ...args)
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
        return
      }

      error.value = err.response?.data?.message || err.message || 'Something went wrong!'
    } finally {
      isLoading.value = false
    }
  }

  function clear() {
    isLoading.value = false
    data.value = null
    error.value = null

    if (controller) {
      controller.abort()
    }
  }

  onBeforeUnmount(() => {
    clear()
  })

  return {
    data,
    error,
    isLoading,
    execute,
    clear
  }
}

export default useFetch
